<template>
    <Drawer ref="drawer" :loading="isLoading" paddingless @closing="reset()">
        <template #title>
            <span v-if="supplier.id && !supplier.is_system" class="tw-mr-2">
                <Tooltip v-if="supplier.xero_contact_id" label="Linked with Xero" position="bottom">
                    <img src="@/images/brands/xero_blue.png" class="icon-sm" alt="Linked with Xero" />
                </Tooltip>

                <Tooltip v-else label="Not linked with Xero" position="bottom">
                    <img src="@/images/brands/xero_blue.png" class="icon-sm grayscale" alt="Not linked with Xero" />
                </Tooltip>
            </span>

            <span>{{ drawerTitle }}</span>
        </template>
        <template v-if="supplier">
            <Banner v-if="supplier.is_system" full-width condensed sticky>
                This is a system generated default contact and cannot be changed.
            </Banner>
            <Banner v-else-if="!canUpdate && !supplier.is_archived" full-width condensed sticky>
                Some fields are hidden as you do not have permission to manage suppliers
            </Banner>
            <Banner v-else-if="supplier.is_archived" type="is-dark" full-width condensed sticky>
                This contact has been archived and can no longer be updated
            </Banner>
            <Tabs
                :tabs="
                    [
                        {
                            title: 'General',
                            name: 'general',
                            headerClass: 'tw-ml-1',
                        },
                        acumaticaIntegration
                            ? {
                                  title: 'Acumatica',
                                  name: 'acumatica',
                              }
                            : null,
                    ].filter((t) => t)
                "
            >
                <template #heading(acumatica)>
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <img
                            src="@/images/brands/acumatica-icon.svg"
                            class="tw-w-4 tw-h-4"
                            :class="{
                                'tw-grayscale': !supplier.acumatica_contact,
                            }"
                            alt="Acumatica"
                        />
                        Acumatica
                    </div>
                </template>
                <template #tab(general)>
                    <div class="p-4">
                        <FormField label="Company Name">
                            <Textbox
                                v-model="supplier.name"
                                placeholder="Company Name"
                                :disabled="!canUpdate"
                                required
                                maxlength="255"
                            />
                        </FormField>
                        <FormField label="Type" class="tw-mt-2">
                            <div class="tw-flex tw-gap-1">
                                <Button
                                    :active="supplier.supplier_type === 0"
                                    size="is-small"
                                    :disabled="!canUpdate"
                                    @click="supplier.supplier_type = 0"
                                >
                                    General
                                </Button>
                                <Button
                                    :active="supplier.supplier_type === 1"
                                    size="is-small"
                                    class="tw-flex tw-gap-2"
                                    :disabled="!canUpdate"
                                    @click="supplier.supplier_type = 1"
                                >
                                    <i class="mdi mdi-ticket-account"></i>
                                    Ticket Seller
                                </Button>
                                <Button
                                    :active="supplier.supplier_type === 2"
                                    size="is-small"
                                    class="tw-flex tw-gap-2"
                                    :disabled="!canUpdate"
                                    @click="supplier.supplier_type = 2"
                                >
                                    <i class="mdi mdi-store"></i>
                                    Trader
                                </Button>
                            </div>
                        </FormField>
                    </div>
                    <hr class="my-0" />
                    <div class="tw-p-4">
                        <FormField label="Postal Address">
                            <Textbox
                                v-model="supplier.address1"
                                placeholder="Address Line 1"
                                :disabled="!canUpdate"
                                maxlength="255"
                            />
                        </FormField>
                        <FormField class="tw-mt-1">
                            <Textbox
                                v-model="supplier.address2"
                                placeholder="Address Line 2"
                                :disabled="!canUpdate"
                                maxlength="255"
                            />
                        </FormField>
                        <FormField class="tw-mt-1">
                            <Textbox
                                v-model="supplier.street"
                                placeholder="Address Line 3"
                                :disabled="!canUpdate"
                                maxlength="255"
                            />
                        </FormField>
                        <FormField class="tw-mt-1">
                            <Textbox
                                v-model="supplier.city"
                                placeholder="City"
                                :disabled="!canUpdate"
                                maxlength="255"
                            />
                        </FormField>
                        <FormField class="tw-mt-1">
                            <Textbox
                                v-model="supplier.county"
                                placeholder="County"
                                :disabled="!canUpdate"
                                maxlength="255"
                            />
                        </FormField>
                        <FormField class="tw-mt-1">
                            <Textbox
                                v-model="supplier.postal_code"
                                placeholder="Postal Code"
                                :disabled="!canUpdate"
                                maxlength="255"
                            />
                        </FormField>
                        <FormField class="tw-mt-1">
                            <Select
                                v-model="supplier.country"
                                placeholder="Country"
                                :disabled="!canUpdate"
                                class="tw-w-full"
                            >
                                <option v-for="country in countries" :key="country" :value="country">
                                    {{ country }}
                                </option>
                            </Select>
                        </FormField>
                    </div>
                    <hr class="my-0" />
                    <div class="p-4">
                        <FormField label="Email Address">
                            <Textbox
                                v-model="supplier.email_address"
                                maxlength="255"
                                placeholder="Email Address"
                                :disabled="!canUpdate"
                            />
                        </FormField>
                        <FormField label="Phone Number" class="tw-mt-2">
                            <Textbox
                                v-model="supplier.phone_number"
                                maxlength="255"
                                placeholder="Phone Number"
                                :disabled="!canUpdate"
                            />
                        </FormField>
                    </div>
                    <hr class="my-0" />
                    <div v-if="canUpdate" class="p-4">
                        <FormField label="Bank Account Name">
                            <Textbox
                                v-model="supplier.bank_account_name"
                                maxlength="255"
                                placeholder="Bank Account Name"
                                :disabled="!canUpdate"
                            />
                        </FormField>
                        <FormField label="Bank Sort Code" class="tw-mt-2">
                            <Textbox
                                v-model="supplier.bank_sort_code"
                                maxlength="255"
                                placeholder="Bank Sort Code"
                                :disabled="!canUpdate"
                            />
                        </FormField>
                        <FormField label="Bank Account Number" class="tw-mt-2">
                            <Textbox
                                v-model="supplier.bank_account_number"
                                maxlength="255"
                                placeholder="Bank Account Number"
                                :disabled="!canUpdate"
                            />
                        </FormField>
                        <FormField label="VAT Number" class="tw-mt-2">
                            <Textbox
                                v-model="supplier.tax_number"
                                maxlength="255"
                                placeholder="VAT Number"
                                :disabled="!canUpdate"
                            />
                        </FormField>
                    </div>
                    <template v-if="canUpdate && supplier.xero_contact_id && store.account.is_connected_to_xero">
                        <hr class="my-0" />
                        <div class="p-4">
                            <Button type="is-primary" class="tw-w-full" @click="syncFromXero()">Sync From Xero</Button>
                        </div>
                    </template>
                </template>
                <template #tab(acumatica)>
                    <div class="tw-p-3">
                        <FormField label="Link with Acumatica Contact" class="tw-mb-3">
                            <div class="tw-flex tw-gap-2">
                                <SmartSearchDropdown
                                    v-if="canUpdate"
                                    v-model="supplier.acumatica_contact_id"
                                    class="tw-flex-1"
                                    placeholder="No Acumatica Contacts"
                                    type="contacts"
                                    :items="acumaticaContacts"
                                    clearable
                                />
                                <Button type="is-success" :disabled="!canSave" @click="updateSupplier()">Save</Button>
                            </div>
                        </FormField>
                        <Banner v-if="!supplier.acumatica_contact" class="tw-text-center">
                            This contact is not linked to an Acumatica contact
                        </Banner>
                        <Banner v-else type="is-info" class="tw-text-center">
                            This contact is current linked to
                            <span class="tw-font-semibold">{{ supplier.acumatica_contact.name }}</span>
                        </Banner>
                    </div>
                </template>
            </Tabs>
        </template>

        <template v-if="supplier && (canDelete || canUpdate || canSave)" #footer>
            <div class="d-flex">
                <Button
                    v-if="canDelete && supplier.id && !supplier.is_archived"
                    class="tw-flex tw-gap-2"
                    @click="deleteSupplier()"
                >
                    <i class="mdi mdi-archive"></i>
                    Archive
                </Button>
                <Button
                    v-if="
                        canDelete &&
                        supplier.id &&
                        !supplier.is_archived &&
                        !supplier.xero_contact_id &&
                        !supplier.is_system
                    "
                    class="tw-ml-auto tw-flex tw-gap-2"
                    @click="mergeSupplier()"
                >
                    <i class="mdi mdi-merge"></i>
                    Merge
                </Button>
                <template v-if="canUpdate">
                    <Button
                        v-if="supplier.xero_contact_id && store.account.is_connected_to_xero"
                        type="is-primary"
                        class="!tw-ml-auto"
                        @click="syncToXero()"
                    >
                        Save & Update In Xero
                    </Button>
                    <template v-else>
                        <Button
                            type="is-success"
                            class="tw-ml-auto tw-flex tw-gap-2"
                            :disabled="!canSave"
                            @click="updateSupplier()"
                        >
                            <i class="mdi mdi-content-save"></i>
                            Save
                        </Button>
                        <Button
                            v-if="store.account.is_connected_to_xero"
                            type="is-primary"
                            class="tw-ml-2"
                            @click="syncToXero()"
                        >
                            Save & Create In Xero
                        </Button>
                    </template>
                </template>
                <Button
                    v-else-if="Auth.can('manage suppliers') && supplier.id && supplier.is_archived"
                    type="is-success"
                    class="tw-ml-auto tw-flex tw-gap-2"
                    @click="restoreSupplier()"
                >
                    <i class="mdi mdi-reload"></i>
                    Restore Contact
                </Button>
            </div>
            <ModalMergeSupplier ref="mergeSupplierModal" />
        </template>
    </Drawer>
</template>
<script setup>
import { useDataStore } from '@/js/stores/DataStore.js';
import Drawer from '@/js/components/Drawer.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import ModalMergeSupplier from '@/js/components/modals/Suppliers/MergeSupplier.vue';
import Button from '@/js/components/controls/Button.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Select from '@/js/components/controls/Select.vue';
import Tabs from '@/js/components/widgets/Tabs.vue';
import SmartSearchDropdown from '@/js/components/controls/SmartSearchDropdown.vue';
import { toast } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import { computed, onMounted, ref, useTemplateRef } from 'vue';

const isLoading = ref(false);
const countries = ref(window.Locales);
const supplier = ref(null);
const store = useDataStore();
const drawer = useTemplateRef('drawer');
const mergeSupplierModal = useTemplateRef('mergeSupplierModal');
const acumaticaIntegration = ref(useDataStore().get('account.integrations.acumatica'));
const acumaticaContacts = ref([]);

onMounted(() => {
    Eventbus.$on('view:supplier', (payload) => viewSupplier(payload));
});

const drawerTitle = computed(() => {
    const action = supplier.value?.id ? 'Edit ' : 'Create ';
    if (!supplier.value || supplier.value.supplier_type === 0) {
        return action + 'Contact';
    } else if (supplier.value.supplier_type === 1) {
        return action + 'Ticket Seller';
    } else if (supplier.value.supplier_type === 2) {
        return action + 'Trader';
    }
    return action + 'Contact';
});

const canUpdate = computed(() => {
    return Auth.can('manage suppliers') && !supplier.value.is_system && !supplier.value.is_archived;
});

const canDelete = computed(() => {
    return Auth.can('delete suppliers') && !supplier.value.is_system;
});

const canSave = computed(() => {
    return Auth.can('manage suppliers') && !supplier.value.is_system && !supplier.value.is_archived;
});

const viewSupplier = (supplierId) => {
    if (!supplierId) {
        supplier.value = null;
        return;
    }
    const selectedSupplier = store.get('account.suppliers.' + supplierId);
    if (!selectedSupplier) {
        toast('Error', 'That supplier could not be found', 'error');
        return;
    }
    supplier.value = JSON.parse(JSON.stringify(selectedSupplier));
    drawer.value?.open();
    getAcumaticaContacts();
};

const updateSupplier = () => {
    if (!supplier.value || supplier.value.is_system) {
        return;
    }
    isLoading.value = true;
    updateStore();
    const isCreating = !supplier.value.id;
    const url = supplier.value.id
        ? route('api.account.contacts.update', { supplier: supplier.value.id })
        : route('api.account.contacts.create');
    const method = supplier.value.id ? 'patch' : 'post';
    axios[method](url, supplier.value)
        .then(() => {
            if (isCreating) {
                capturePosthogEvent('contact_created');
            }
            toast(
                'Supplier Saved',
                'Your changes to ' + supplier.value.name + ' have been saved successfully',
                'success'
            );
            drawer.value?.close();
            Eventbus.$emit('reload:suppliers-table');
            Eventbus.$emit('refresh:BudgetEntry');
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const updateStore = () => {
    store.set('account.suppliers.' + supplier.value.id, supplier.value);
};

const deleteSupplier = () => {
    if (!supplier.value || supplier.value.is_system) {
        return;
    }
    swal.fire({
        title: 'Are you sure?',
        text: 'Archiving this supplier will mean that it can no longer be selected. Are you sure you want to archive this supplier?',
        showCancelButton: true,
        confirmButtonColor: '#4EA5D9',
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Archive',
    }).then((result) => {
        if (!result.value) {
            return;
        }
        isLoading.value = true;
        axios
            .delete(route('api.account.contacts.delete', { supplier: supplier.value.id }))
            .then(() => {
                store.set('account.suppliers.' + supplier.value.id + '.is_archived', true);
                toast('Supplier Archived', supplier.value.name + ' has been archived successfully', 'success');
                drawer.value.close();
                Eventbus.$emit('reload:suppliers-table');
            })
            .finally(() => {
                isLoading.value = false;
            });
    });
};

const mergeSupplier = () => {
    if (!supplier.value || supplier.value.is_system) {
        return;
    }
    drawer.value.close();
    mergeSupplierModal.value.open({
        baseSupplier: supplier.value,
    });
};

const restoreSupplier = () => {
    if (!supplier.value || supplier.value.is_system) {
        return;
    }
    isLoading.value = true;
    axios
        .post(route('api.account.contacts.restore', { supplier: supplier.value.id }))
        .then(() => {
            store.set('account.suppliers.' + supplier.value.id + '.is_archived', false);
            toast('Supplier Restored', supplier.value.name + ' has been restored successfully', 'success');
            drawer.value.close();
            Eventbus.$emit('reload:suppliers-table');
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const syncFromXero = () => {
    if (!supplier.value || !supplier.value.xero_contact_id) {
        return;
    }
    isLoading.value = true;
    axios
        .post(route('api.account.contacts.syncFromXero', { supplier: supplier.value.id }))
        .then(() => {
            toast(
                'Supplier Synced from Xero',
                supplier.value.name + ' has been successfully synced from Xero',
                'success'
            );
            drawer.value.close();
            Eventbus.$emit('reload:suppliers-table');
        })
        .catch(() => {
            toast('Unable to Sync Supplier from Xero', supplier.value.name + ' could not be synced from Xero', 'error');
            drawer.value.close();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const syncToXero = () => {
    if (!supplier.value || supplier.value.is_system) {
        return;
    }
    isLoading.value = true;
    const url = supplier.value.id
        ? route('api.account.contacts.syncToXero', { supplier: supplier.value.id })
        : route('api.account.contacts.createInXero');
    axios
        .post(url, supplier.value)
        .then(() => {
            toast(
                'Supplier Saved',
                'Your changes to ' + supplier.value.name + ' have been saved successfully and updated in Xero',
                'success'
            );
            drawer.value.close();
            Eventbus.$emit('reload:suppliers-table');
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const reset = () => {
    supplier.value = null;
    isLoading.value = false;
};

const getAcumaticaContacts = async () => {
    if (!acumaticaIntegration.value) return;
    isLoading.value = true;
    const response = await axios.get(route('api.account.integrations.acumatica.contacts'));
    acumaticaContacts.value = response.data;
    isLoading.value = false;
};
</script>
